import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import * as organisationActions from "../actions/organisationActions";
import * as tournamentActions from "../actions/tournamentActions";

const initialState = {
  organisations: [],
  selectedOrganisation: null,
  status: "idle",
  error: null,
  loading: false,
  totalPages: 0,
  totalResults: 0,
  page: 1,
  limit: 10,
  isUpdatingSelected: false,
  updateSelectedError: null,
  isAuthenticated: false,
  creatingTournament: false,
  createTournamentError: null,
};

export const fetchOrganisations = createAsyncThunk(
  "organisations/fetchOrganisations",
  async (params, { rejectWithValue }) => {
    try {
      if (!params.search) {
        delete params.search;
      }
      const response = await organisationActions.getOrganisations(params);
      return {
        ...response,
        append: params.append,
        organisations: response.results,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createOrganisation = createAsyncThunk(
  "organisations/createOrganisation",
  async (organisationData, { rejectWithValue }) => {
    try {
      return await organisationActions.createOrganisation(organisationData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateOrganisation = createAsyncThunk(
  "organisations/updateOrganisation",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      return await organisationActions.updateOrganisation(id, updateData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteOrganisation = createAsyncThunk(
  "organisations/deleteOrganisation",
  async (id, { rejectWithValue }) => {
    try {
      return await organisationActions.deleteOrganisation(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSelectedOrganisation = createAsyncThunk(
  "organisations/updateSelectedOrganisation",
  async ({ id }, { rejectWithValue }) => {
    try {
      if (!id) {
        throw new Error("Organisation ID is required");
      }
      const response = await organisationActions.updateSelectedOrganisation(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update selected organisation"
      );
    }
  }
);

export const getSelectedOrganisation = createAsyncThunk(
  "organisations/getSelectedOrganisation",
  async (_, { rejectWithValue }) => {
    try {
      const response = await organisationActions.getSelectedOrganisation();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to get selected organisation"
      );
    }
  }
);

export const createTournament = createAsyncThunk(
  "tournament/createTournament",
  async (tournamentData, { rejectWithValue }) => {
    try {
      return await tournamentActions.createTournament(tournamentData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const UPDATE_SELECTED_ORGANISATION_REQUEST =
  "organisation/updateSelectedOrganisationRequest";
const UPDATE_SELECTED_ORGANISATION_SUCCESS =
  "organisation/updateSelectedOrganisationSuccess";
const UPDATE_SELECTED_ORGANISATION_FAILURE =
  "organisation/updateSelectedOrganisationFailure";

const organisationSlice = createSlice({
  name: "organisations",
  initialState,
  reducers: {
    selectOrganisation: (state, action) => {
      state.selectedOrganisation = action.payload;
    },
    [UPDATE_SELECTED_ORGANISATION_REQUEST]: (state) => {
      state.isUpdatingSelected = true;
      state.updateSelectedError = null;
    },
    [UPDATE_SELECTED_ORGANISATION_SUCCESS]: (state, action) => {
      state.isUpdatingSelected = false;
      state.selectedOrganisation = action.payload;
    },
    [UPDATE_SELECTED_ORGANISATION_FAILURE]: (state, action) => {
      state.isUpdatingSelected = false;
      state.updateSelectedError = action.payload;
    },
    resetTournamentState: (state) => {
      state.creatingTournament = false;
      state.createTournamentError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch organisations
      .addCase(fetchOrganisations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrganisations.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.append) {
          state.organisations = [
            ...state.organisations,
            ...action.payload.results,
          ];
        } else {
          state.organisations = action.payload.results;
        }
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchOrganisations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create organisation
      .addCase(createOrganisation.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOrganisation.fulfilled, (state, action) => {
        state.loading = false;
        state.organisations = [...state.organisations, action.payload];
      })
      .addCase(createOrganisation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update organisation
      .addCase(updateOrganisation.fulfilled, (state, action) => {
        const index = state.organisations.findIndex(
          (org) => org.id === action.payload.id
        );
        if (index !== -1) {
          state.organisations[index] = action.payload;
        }
        state.error = null;
      })
      // Delete organisation
      .addCase(deleteOrganisation.fulfilled, (state, action) => {
        state.organisations = state.organisations.filter(
          (org) => org.id !== action.payload.id
        );
        state.error = null;
      })
      .addCase(updateSelectedOrganisation.pending, (state) => {
        state.isUpdatingSelected = true;
        state.updateSelectedError = null;
      })
      .addCase(updateSelectedOrganisation.fulfilled, (state, action) => {
        state.isUpdatingSelected = false;
        state.selectedOrganisation = action.payload;
        state.status = "idle";
      })
      .addCase(updateSelectedOrganisation.rejected, (state, action) => {
        state.isUpdatingSelected = false;
        state.updateSelectedError = action.payload;
      })
      .addCase(getSelectedOrganisation.fulfilled, (state, action) => {
        if (action.payload) {
          state.selectedOrganisation = action.payload;
        } else {
          // If no organisation found, clear selection
          state.selectedOrganisation = null;
        }
      })
      .addCase(getSelectedOrganisation.rejected, (state, action) => {
        // If getting selected organisation fails, clear it
        state.selectedOrganisation = null;
        state.error = action.payload;
      })
      .addCase(createTournament.pending, (state) => {
        state.creatingTournament = true;
        state.createTournamentError = null;
      })
      .addCase(createTournament.fulfilled, (state, action) => {
        state.creatingTournament = false;
      })
      .addCase(createTournament.rejected, (state, action) => {
        state.creatingTournament = false;
        state.createTournamentError = action.payload;
      });
  },
});

export const { selectOrganisation, resetTournamentState } =
  organisationSlice.actions;
export default organisationSlice.reducer;
